import { useEffect, useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { LogIn, ShieldCheck, Mail, ArrowLeft, Fingerprint } from "lucide-react";
import { AccentButton, Button, Input, Label } from "@wa/ui";
import { useT } from "../i18n/I18nProvider";
import { login, isMfaRequired, type StaffUser, type LoginResult } from "../lib/auth";
import { mfaApi, type MfaChallengeInfo } from "../lib/mfa";
import { passkeyApi, isWebAuthnReady, isWebAuthnCancel } from "../lib/passkey";
import { BrandLogo } from "../components/BrandLogo";
import { AppBackground } from "../components/AppBackground";
import { LanguageMenu } from "../components/LanguageMenu";

export function LoginPage({ onSuccess }: { onSuccess: (u: StaffUser) => void }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  // Passkey (biometric) sign-in. The button is drawn only when the browser can
  // actually run a ceremony, so it is never a control that does nothing.
  const passkeySupported = isWebAuthnReady();
  const [bioBusy, setBioBusy] = useState(false);
  const [bioError, setBioError] = useState(false);

  // The MFA challenge (the second factor)
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState(false);
  const [mfaInfo, setMfaInfo] = useState<MfaChallengeInfo | null>(null);
  const [emailSent, setEmailSent] = useState<string | null>(null);

  useEffect(() => {
    if (!pendingToken) {
      setMfaInfo(null);
      setEmailSent(null);
      return;
    }
    mfaApi.challengeInfo(pendingToken).then(setMfaInfo).catch(() => setMfaInfo(null));
  }, [pendingToken]);

  function handleResult(res: LoginResult) {
    if (isMfaRequired(res)) setPendingToken(res.pendingToken);
    else onSuccess(res);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(false);
    try {
      handleResult(await login(email, password));
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  async function biometric() {
    setBioBusy(true);
    setBioError(false);
    try {
      // The typed email, when there is one, lets the server send
      // allowCredentials, so the browser goes straight to Touch ID.
      handleResult(await passkeyApi.login(email.trim() || undefined));
    } catch (err) {
      // A cancel or a timeout is not a fault worth a banner.
      if (!isWebAuthnCancel(err)) setBioError(true);
    } finally {
      setBioBusy(false);
    }
  }

  async function submitMfa(e: FormEvent) {
    e.preventDefault();
    if (!pendingToken) return;
    setBusy(true);
    setMfaError(false);
    try {
      onSuccess(await mfaApi.verify(pendingToken, mfaCode.trim()));
    } catch {
      setMfaError(true);
    } finally {
      setBusy(false);
    }
  }

  async function sendMfaEmail() {
    if (!pendingToken) return;
    setBusy(true);
    try {
      const res = await mfaApi.emailSendLogin(pendingToken);
      setEmailSent(res.sentTo ?? "");
    } finally {
      setBusy(false);
    }
  }

  function cancelMfa() {
    setPendingToken(null);
    setMfaCode("");
    setMfaError(false);
  }

  return (
    <main className="relative isolate flex min-h-dvh items-center justify-center overflow-hidden px-4 py-12">
      <AppBackground intensity={55} />

      <div className="absolute right-4 top-4 z-10">
        <LanguageMenu />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="mb-6 flex justify-center">
          <BrandLogo className="h-7 w-auto text-foreground" />
        </div>

        <div className="card-aurora-border rounded-xl bg-card/90 p-6 shadow-soft backdrop-blur">
          {pendingToken ? (
            <form onSubmit={submitMfa} className="flex flex-col gap-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <ShieldCheck className="size-8 text-primary" />
                <h1 className="text-xl font-bold tracking-tight">{t("mfa.challengeTitle")}</h1>
                <p className="text-sm text-muted-foreground">{t("mfa.challengeHint")}</p>
              </div>
              <Input
                autoFocus
                inputMode="text"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder={t("mfa.codePlaceholder")}
                className="text-center font-mono text-lg tracking-widest"
              />
              {mfaError && <p className="text-sm text-destructive">{t("mfa.invalidCode")}</p>}
              <AccentButton glow="aurora" type="submit" size="lg" loading={busy} disabled={!mfaCode.trim()} className="w-full">
                {t("mfa.verify")}
              </AccentButton>

              {mfaInfo?.methods.email_otp &&
                (emailSent !== null ? (
                  <p className="text-center text-xs text-muted-foreground">
                    {t("mfa.emailSent", { email: emailSent || t("mfa.yourEmail") })}
                  </p>
                ) : (
                  <Button type="button" variant="outline" size="sm" onClick={sendMfaEmail} disabled={busy} className="self-center">
                    <Mail className="mr-1 size-4" />
                    {t("mfa.sendEmailCode")}
                  </Button>
                ))}

              <Button type="button" variant="ghost" size="sm" onClick={cancelMfa} className="self-center">
                <ArrowLeft className="mr-1 size-4" />
                {t("mfa.back")}
              </Button>
            </form>
          ) : (
            <>
              <div className="mb-1 flex items-center gap-3">
                <div className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <LogIn className="size-5" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight">{t("login.title")}</h1>
              </div>
              <p className="mb-6 text-sm text-muted-foreground">{t("login.subtitle")}</p>

              <form onSubmit={submit} className="flex flex-col gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">{t("login.email")}</Label>
                  <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">{t("login.password")}</Label>
                  <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
                </div>
                {error && <p className="text-sm text-destructive">{t("login.error")}</p>}
                <AccentButton glow="aurora" type="submit" size="lg" loading={busy} className="mt-1 w-full">
                  {busy ? t("login.busy") : t("login.submit")}
                </AccentButton>
              </form>

              {passkeySupported && (
                <>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-xs text-muted-foreground">{t("login.or")}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    className="w-full"
                    loading={bioBusy}
                    disabled={busy}
                    onClick={biometric}
                  >
                    <Fingerprint className="mr-1 size-4" />
                    {t("login.passkey")}
                  </Button>
                  {bioError && (
                    <p className="mt-2 text-center text-sm text-destructive">{t("login.passkeyError")}</p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </motion.div>
    </main>
  );
}
